import { Environment } from '../environment';
import moment from 'moment/moment';
import { BehaviorSubject } from 'rxjs';
import { MqttClient } from 'mqtt';
import throttle from 'lodash/throttle';
import { IClientOptions } from 'mqtt/types/lib/client';
import { Moment } from 'moment';
import { createFingerprint } from '../hooks/device-fingerprint';
import React from 'react';
import { XV2 } from '../XV2';

const log = Boolean(localStorage.getItem('debug_iot'))
    ? Function.prototype.bind.call(console.log, [], '[Iot$]')
    : () => {};

const client$ = new BehaviorSubject<MqttClient | undefined>(undefined);
const topics$ = new BehaviorSubject<Record<string, { subscribed: boolean; count: number }>>({});
const lastEvent$ = new BehaviorSubject<{ topic: string; data? } | undefined>(undefined);
const lastActivityDate$ = new BehaviorSubject<Moment>(moment.utc());

let pingTopic = `ping/${createFingerprint()}`;
let shouldReconnect = false;

(window as any).debugIot = () => ({
    pingTopic: pingTopic,
    topics: topics$.getValue(),
    client: client$.getValue(),
});

export namespace Iot$ {
    export function init(deviceFingerprint?) {
        pingTopic = deviceFingerprint ? `ping/device/${deviceFingerprint}` : `ping/unknown/${createFingerprint()}`;
        startConnectionInterval();
        getUrlAndConnect();
    }

    export function Subscribe({ topic, callback }: { topic; callback }) {
        useSubscribe(topic, callback);
        return null;
    }

    export function useSubscribe(_topic, callback) {
        const topic = getTopic(_topic);

        React.useEffect(() => {
            if (!_topic) return;

            updateTopicCount(topic, 1);

            const sub = lastEvent$.subscribe((event) => {
                if (event?.topic === topic) {
                    callback(event?.data);
                }
            });

            return () => {
                sub.unsubscribe();
                updateTopicCount(topic, -1);
            };
        }, [topic, callback]);

        return null;
    }

    export function publish(topic, data?) {
        client$.getValue()?.publish(topic, '');
    }
}

client$.subscribe((client) => {
    if (!client) return;

    client.on('connect', () => {
        log('[connect]');
        lastActivityDate$.next(moment.utc());
    });

    client.on('message', (topic, message) => {
        log('[message]', topic);
        lastEvent$.next({ topic: topic, data: parseMessage(message) });
        lastActivityDate$.next(moment.utc());
    });

    client.on('error', (error) => {
        log('[error]', error);
    });

    client.on('disconnect', (packet) => {
        log('[disconnect]', packet);
    });

    client.on('close', () => {
        log('[close]');
        client.end(true);
    });

    client.on('offline', () => {
        log('[offline]');
    });

    client.subscribe(pingTopic);

    client.on('end', () => {
        log('[end]');
        client$.next(undefined);
        getUrlAndConnect();
    });
});

client$.subscribe((client) => {
    if (!client) return;

    topics$.subscribe((topics) => {
        const next = { ...topics };
        let changed = false;

        Object.keys(next).forEach((topic) => {
            const { subscribed, count } = next[topic];

            if (count > 0 && !subscribed) {
                log('sub   :', `"${topic}"`);
                client.subscribe(topic);
                next[topic].subscribed = true;
                changed = true;
            }

            if (count === 0 && subscribed) {
                log('unsub :', `"${topic}"`);
                client.unsubscribe(topic);
                changed = true;
                next[topic].subscribed = false;
            }
        });

        if (changed) {
            topics$.next(next);
        }
    });
});

const getUrlAndConnect = throttle(() => {
    XV2.req(XV2.API.V1ProviderAwsGetIotUrl.url, {})
        .then(({ url }) => {
            client$.next(
                connect(url, {
                    keepalive: 10 * 1000,
                    connectTimeout: 60 * 1000,
                }),
            );
        })
        .catch((error) => {
            console.error(error);
        });
}, 5000);

let connectionInterval;

function startConnectionInterval() {
    window.clearInterval(connectionInterval);

    connectionInterval = setInterval(() => {
        const activeClient = client$.getValue();
        const date = lastActivityDate$.getValue();
        const diffLastEventSeconds = Math.round(moment.utc().diff(date, 'millisecond') / 1000);

        log('diffLastEventSeconds', diffLastEventSeconds);

        if (diffLastEventSeconds >= 35) {
            if (activeClient) {
                activeClient.end(true);
            } else {
                getUrlAndConnect();
            }
        } else if (diffLastEventSeconds >= 30 && activeClient) {
            activeClient.publish(pingTopic, '');
        }
    }, 5 * 1000);
}

function updateTopicCount(topic: string, count: number) {
    const value = topics$.getValue();

    topics$.next({
        ...value,
        [topic]: {
            count: (value[topic]?.count ?? 0) + count,
            subscribed: value[topic]?.subscribed ?? false,
        },
    });
}

function connect(brokerUrl?: string | any, opts?: IClientOptions): MqttClient {
    return (window as any).mqtt.connect(brokerUrl, opts);
}

function getTopic(topic) {
    return Environment.IS_DEV ? `DEV_${topic}` : topic;
}

function parseMessage(message) {
    const messageAsString = message.toString();

    try {
        return JSON.parse(messageAsString);
    } catch (error) {
        return messageAsString;
    }
}

module?.hot?.dispose?.(() => {
    client$.getValue()?.end(true);
    window.clearInterval(connectionInterval);
});
